import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import Hashtag from "components/Common/Hashtag";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Col, Container, Label, Row, Spinner } from 'reactstrap';
import { putImageLibrary } from "store/thunks";
import * as Yup from "yup";

export interface Props {
    itemEdit?: any,
    triggerRefresh?: (item_new?: any) => void;
}

registerLocale("en", en);
registerLocale("ko", ko);

const FILE_SIZE = 11 * 1024 * 1024; // 11MB
const SUPPORTED_FORMATS = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const LibraryEditForm = ({
    itemEdit = null,
    triggerRefresh,
}: Props) => {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleSubmit = async (values: any) => {
        try {
            setIsLoading((_prev) => true);
            const data = {
                file_name: itemEdit?.file_name || '',
                tags: (values?.tags || [])?.map((url: any) => url?.text),
            };
            const response: any = await putImageLibrary(itemEdit?.id, data);
            if (response?.code === 200) {
                setIsLoading((_prev) => false);
                toast(
                    `${t("The process has been completed.")}`,
                    CONFIG_OPTION_TOAST_NORMAL
                );
                triggerRefresh && triggerRefresh({ ...itemEdit, tags: response?.data?.tag_names });
            } else {
                setIsLoading((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        tags: Yup.array().of(
            Yup.object().shape({
                id: Yup.string(),
                text: Yup.string(),
            })
        ).min(1, t('Please enter at least 1 tag')),
    });

    const formik = useFormik({
        initialValues: {
            tags: [],
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    useEffect(() => {
        const _tags = (itemEdit?.tags || [])?.map((tag: string) => ({ id: tag, text: tag }));
        formik.setFieldValue("tags", _tags);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemEdit]);

    return (
        <React.Fragment>
            <div>
                <Container>
                    <Row>
                        <form onSubmit={formik.handleSubmit}>
                            <div style={{ margin: '0 auto' }}>
                                <Row>
                                    <Col sm={12} md={12} xl={12}>
                                        <img className="gallery-img img-fluid mx-auto" style={{ minHeight: '300px', borderRadius: '4px' }} src={itemEdit?.thumbnail_url || ''} alt={t('No Image')} />
                                    </Col>
                                    <Col sm={12} md={12} xl={12} className='mt-3 '>
                                        <Label htmlFor="tags" className="form-label">{t('Tags')} <span className="text-danger"> *</span></Label>
                                        <Hashtag
                                            name="tags"
                                            placeholder={`${t("Tags")}...`}
                                            suggestions={[]}
                                            initialValue={formik?.values?.tags || []}
                                            onChangeTag={(event: any) => {
                                                formik.setFieldValue("tags", event);
                                            }}
                                            idSelected="hashtags-CampaignFormMulti"
                                            maxHashtags={2000}
                                            maxHeight="150px"
                                        />
                                        {formik.touched.tags && formik.errors.tags ? (
                                            <div className="text-danger mt-2">{formik.errors.tags}</div>
                                        ) : null}
                                    </Col>
                                    <Col sm={12} md={12} xl={12} className='mt-4 pt-1 text-end'>
                                        <button
                                            style={{ width: '160px' }}
                                            type="submit"
                                            disabled={isLoading}
                                            className="btn btn-primary fs-14 rounded-pill">
                                            {isLoading ? <Spinner size="sm me-2" ></Spinner> : <></>}
                                            {t('Button Update Image')}
                                        </button>
                                    </Col>
                                </Row>
                            </div>
                        </form>
                    </Row >
                </Container >
            </div>
            <ToastContainer closeButton={false} newestOnTop={false} pauseOnFocusLoss autoClose={2000} limit={2} />
        </React.Fragment >
    );
};

export default LibraryEditForm;
